import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@localagent/ui/components/dialog';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { Label } from '@localagent/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@localagent/ui/components/select';
import { toast } from '@localagent/ui/components/sonner';
import { useTestAction } from '../../hooks/useIntegrations';

interface TestActionItem {
  action_key: string;
  name: string;
  http_method: string;
  path_template: string;
}

interface TestActionModalProps {
  connectionId: string;
  actions: TestActionItem[];
  open: boolean;
  onClose: () => void;
}

export function TestActionModal({ connectionId, actions, open, onClose }: TestActionModalProps) {
  const [selectedAction, setSelectedAction] = useState('');
  const [paramsJson, setParamsJson] = useState('{}');
  const testAction = useTestAction();

  const handleSubmit = () => {
    try {
      const parameters = JSON.parse(paramsJson) as Record<string, unknown>;
      testAction.mutate(
        { connectionId, params: { action_key: selectedAction, parameters } },
        {
          onSuccess: (data) => {
            if (data.status === 'success') {
              toast.success(`Action executed successfully (${data.http_status_code ?? 'OK'})`);
            } else {
              toast.error(`Action failed: ${data.error_message ?? 'Unknown error'}`);
            }
          },
          onError: (err) => {
            toast.error(`Test failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
          },
        },
      );
    } catch {
      toast.error('Invalid JSON in parameters');
    }
  };

  const handleClose = () => {
    testAction.reset();
    setSelectedAction('');
    setParamsJson('{}');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Test Action</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Action</Label>
            <Select value={selectedAction} onValueChange={setSelectedAction}>
              <SelectTrigger>
                <SelectValue placeholder="Select an action..." />
              </SelectTrigger>
              <SelectContent>
                {actions.map((a) => (
                  <SelectItem key={a.action_key} value={a.action_key}>
                    {a.name} ({a.http_method} {a.path_template})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Parameters (JSON)</Label>
            <textarea
              className="w-full rounded-md bg-muted px-3 py-2 font-mono text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              rows={6}
              value={paramsJson}
              onChange={(e) => setParamsJson(e.target.value)}
            />
          </div>
          {testAction.data && (
            <div className="rounded-md bg-muted p-4">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-medium">Result:</span>
                <Badge variant={testAction.data.status === 'success' ? 'success' : 'destructive'}>
                  {testAction.data.status}
                </Badge>
                {testAction.data.http_status_code && (
                  <Badge variant="outline">{testAction.data.http_status_code}</Badge>
                )}
                <span className="text-xs text-muted-foreground">{testAction.data.duration_ms}ms</span>
              </div>
              {testAction.data.error_message && (
                <p className="mb-2 text-sm text-destructive">{testAction.data.error_message}</p>
              )}
              {testAction.data.response_body && (
                <pre className="max-h-48 overflow-auto rounded bg-background p-3 font-mono text-xs">{testAction.data.response_body}</pre>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!selectedAction || testAction.isPending}>
            {testAction.isPending ? 'Running...' : 'Execute'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
